import {
  Button,
  Card,
  PageContainer,
  PageHeader,
  Typography,
  useToast,
} from '@sl/react-datafabric-components';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useNavigate } from 'react-router-dom';
import { useAgentContext } from '../../../common/context/agent.provider';
import { useFetchPlansQuery } from '../../../common/redux/services/plans';
import { useFetchProfileQuery } from '../../../common/redux/services/profile';
import CreditsPage from './components/credits/credits';
import { useDeleteSubscriptionWithConfirmation } from './components/use-delete-company-subscription-with-confirmation';
import styles from './subscriptions.module.css';

export type Subscription = {
  id: string;
  plan: {
    name: string;
    roleID: string;
    priority: number;
    price: number;
    trial: boolean;
    type: string;
    nTokens: number;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    description: string;
    metadata: {
      subscription: {
        productID: string;
      };
    };
    id: string;
  };
  itemName: string;
  itemType: string;
  itemID: string;
  startDate: string;
  renewalDate: string;
  status: string;
  currentTokens: number;
  autoTopUp?: boolean;
  autoTopUpValue?: number;
};

const Content = ({
  name,
  description,
}: {
  name: string;
  description?: string;
}) => (
  <div className='w-full'>
    {/* White Name */}
    <Typography type={3} className='font-bold text-white mb-2'>
      {name.toUpperCase()}
    </Typography>

    {/* Full-Height Description */}
    <div className='w-full mt-2'>
      <Typography className='text-black text-justify'>{description}</Typography>
    </div>
  </div>
);

export default function SubscriptionsPage() {
  const navigate = useNavigate();

  const { data } = useFetchPlansQuery({});
  const { agentSubscriptions } = useAgentContext();

  const { data: user } = useFetchProfileQuery('');
  const { deleteSubscriptionWithConfirmation } =
    useDeleteSubscriptionWithConfirmation();
  const { showToast } = useToast();

  // Check isSuperAdmin from multiple possible locations
  const isSuperAdmin =
    user?.data?.isSuperAdmin === true ||
    user?.data?.userRoles?.some((role: any) => role.isSuperAdmin === true) ||
    user?.data?.roles?.some((role: any) => role.isSuperAdmin === true);
  const subscriptionsPlans = agentSubscriptions?.flatMap((sub) => ({
    planID: sub.plan?.id,
    plan: sub.plan,
  }));

  const subscribedPlanIDs = subscriptionsPlans?.map((sub) => sub.plan.id);

  const highestSubscribedPriority = Math.min(
    ...(subscriptionsPlans?.map((sub) => sub?.plan?.priority) || [Infinity])
  );

  return (
    <PageContainer>
      <PageHeader
        title='Subscriptions'
        description='Learn more about our subscription options.'
      />

      {data?.data && data.data.length > 0 && (
        <div className='p-1 flex flex-wrap gap-4 justify-content-evenly'>
          <Tooltip
            target='.tooltip-report'
            pt={{ root: { className: 'max-w-15rem' } }}
          />
          {[...data.data]
            .sort((a, b) => a.priority - b.priority)
            .map(
              ({ id, name, priority, trial, roleID, description, price }) => {
                const isActivePlan = subscribedPlanIDs?.includes(id);
                const priorityColor = isActivePlan
                  ? 'rgba(34, 197, 94, 0.3)' // Green background for active plan
                  : `hsla(7, 4%, ${92 - priority * 8.5}%, 0.2)`; // Original priority-based color

                const header = (
                  <img
                    alt='Card Header'
                    src='/logo-small.png'
                    className={classNames(
                      styles.ghost,
                      'w-5 mx-auto p-2 rounded-t-lg'
                    )}
                  />
                );
                let footer: JSX.Element | null = null;
                footer = null;

                if (
                  isSuperAdmin ||
                  !subscribedPlanIDs?.length ||
                  (trial && subscribedPlanIDs?.includes(id))
                ) {
                  footer = null;
                } else if (trial) {
                  footer = <div className='flex justify-between mt-6'></div>;
                } else if (subscribedPlanIDs?.includes(id)) {
                  footer = (
                    <div className='flex justify-between mt-2'>
                      <Button
                        label='Manage Subscription'
                        icon='pi pi-wrench'
                        severity='success'
                        // style={{ background: 'white', color: '#fff' }}
                        onClick={() =>
                          navigate('manage-subscription', {
                            state: { selectedPlan: { id, name, roleID } },
                          })
                        }
                      />
                    </div>
                  );
                } else if (priority < highestSubscribedPriority) {
                  footer = (
                    <div className='flex justify-between mt-2'>
                      <Button
                        label='Downgrade'
                        icon='pi pi-arrow-down'
                        severity='secondary'
                        onClick={() =>
                          navigate('subscribe', {
                            state: {
                              selectedPlan: { id, name, roleID },
                              downgrade: true,
                            },
                          })
                        }
                      />
                    </div>
                  );
                } else {
                  footer = (
                    <div className='flex justify-between mt-2'>
                      <Button
                        label='Upgrade'
                        icon='pi pi-arrow-up'
                        severity='info'
                        onClick={() =>
                          navigate('subscribe', {
                            state: { selectedPlan: { id, name, roleID } },
                          })
                        }
                      />
                    </div>
                  );
                }

                return (
                  <Card
                    key={id}
                    subTitle={
                      price
                        ? `Pricing from ${price}€`
                        : 'Your starter program to test our features'
                    }
                    footer={footer}
                    header={header}
                    pt={{
                      header: { className: 'flex align-items-center pt-4' },
                    }}
                    className={`md:w-25rem shadow-6 hover:shadow-8 border-round-2xl ${
                      isActivePlan ? styles.activePlan : ''
                    }`}
                    style={{
                      backgroundColor: priorityColor,
                      gridColumn: 'span 1',
                    }}
                  >
                    {isActivePlan && (
                      <div className={styles.activeBadge}>ACTIVE PLAN</div>
                    )}
                    <Content name={name} description={description} />
                  </Card>
                );
              }
            )}
        </div>
      )}

      <div className='border-1 surface-border border-round-lg mt-6 mx-2 p-5 pb-2'>
        <CreditsPage />
      </div>
    </PageContainer>
  );
}
